/** How a sentence is turned into the address of its own page. */
export interface HanziSlugProperties {
  /** How many characters a slug may run to. {@link defaultHanziSlugLength}. */
  readonly maxLength?: number;

  /** What to call a sentence that slugs to nothing at all. */
  readonly fallback?: string;
}

/**
 * How long a slug may be, in characters rather than in bytes.
 *
 * Measured over the 13,943 sentences in the library: the median is 8 characters
 * and the longest is 34, so this cuts nothing that exists and is here for the
 * sentence somebody adds later. 40 characters is 360 of percent-encoded URL,
 * which is inside every limit that matters — S3 caps a key at 1,024 bytes and
 * the shortest browser limit is some thousands.
 *
 * A cut slug can collide with another cut slug sharing its opening. That is the
 * same collision two whole sentences can have and is settled the same way, by
 * `LijuSlugs`, which is what makes truncating safe rather than a way to lose a
 * page.
 */
export const defaultHanziSlugLength = 40;

/** What a sentence is called when nothing of it survives slugging. */
export const hanziSlugFallback = "liju";

/**
 * A string as its characters, rather than as its UTF-16 code units.
 *
 * `slug.length` and `slug.slice` count code units, and every Han character
 * outside the basic block is two of them: cutting a slug that way would take
 * half of 𠮟 and leave a lone surrogate to name a directory and appear in a
 * URL. Code points are the unit a slug is measured and cut in, so this is the
 * one place that says so.
 *
 * The lint rule this turns off is about grapheme clusters, and is right in
 * general: an emoji built from several code points, or a letter followed by a
 * combining accent, is one character to a reader and several here. Neither can
 * reach this. The input is Chinese sentences, where a code point is a
 * character; {@link hanziSlug} normalises to NFC first, which composes what can
 * be composed; and `Intl.Segmenter`, which the rule suggests, would bring
 * locale-aware segmentation to a decision that must be the same everywhere,
 * since it names files and addresses.
 */
export function characters(text: string): readonly string[] {
  // oxlint-disable-next-line typescript/no-misused-spread -- code points are the unit, deliberately: see above
  return [...text];
}

/**
 * Everything a slug drops: punctuation, symbols, separators and whitespace.
 *
 * Both alphabets' worth. 。！？ and ，are as much punctuation as . ! ? and , and
 * a sentence ending in one is the same sentence as one ending in the other:
 * 我们开始吧。 and 我们开始吧！ are two rows in the corpus and one address, which
 * `LijuSlugs` then tells apart. Twelve pairs in the library are like that.
 */
const dropped = /[\p{P}\p{S}\p{Z}\s]/gu;

/**
 * A sentence as the address of its own page.
 *
 * **The sentence itself, not its reading.** The addresses used to be numbered
 * pinyin — `ai4-hua1-de5-ren2-shi4-xing4fu2-de5-dncr` — which is a spelling of
 * the sentence that nobody searches for and nobody reads. 爱花的人是幸福的 is
 * what a reader looking for this page would type, and it is what the page is
 * about, so it is what the page is called.
 *
 * Nothing is transliterated, lowercased away or replaced with a hyphen. What
 * comes out is the characters the sentence is written with, punctuation
 * removed, and a slug is therefore reversible by eye: an address in a log or a
 * search result says which sentence it is.
 *
 * Normalised to NFC, which costs nothing and closes a real hole. macOS hands
 * back decomposed filenames where Linux does not, so a slug written on one and
 * looked up on the other could differ by nothing a person can see. Han
 * characters do not decompose and the corpus has two sentences with Latin
 * letters in them, so this has no work to do today and will the first time one
 * arrives with an accent on it.
 *
 * Latin is lowercased, digits are kept. A digit is part of the sentence —
 * 他是1996年毕业的 — and dropping it would leave the address saying something
 * the sentence does not.
 */
export function hanziSlug(
  sentence: string,
  properties: HanziSlugProperties = {},
): string {
  const kept = sentence.normalize("NFC").replaceAll(dropped, "").toLowerCase();

  const slug = characters(kept)
    .slice(0, properties.maxLength ?? defaultHanziSlugLength)
    .join("");

  return slug === "" ? (properties.fallback ?? hanziSlugFallback) : slug;
}
